import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from '../../lib/supabase'

export interface BookingRow {
  id?: string
  date: string
  time: string
  datetime_iso: string
  timezone?: string | null
  created_at?: string | null

  // Booker identity (added Sub-Phase 1A / F5; replaces the old
  // contact-INSERT path).
  booker_email?: string | null
  booker_name?: string | null

  // Google Calendar event push outcome (added Phase 0 Task 2).
  google_event_id?: string | null
  google_event_status?: 'created' | 'failed' | 'skipped' | null
  google_event_error?: string | null

  // Provider-neutral calendar event outcome (Google or Zoho). Prefer
  // these over google_event_*; the latter stay for rows written before
  // the provider columns existed.
  provider?: 'google' | 'zoho' | null
  provider_event_id?: string | null
  provider_event_status?:
    | 'created'
    | 'updated'
    | 'reinserted'
    | 'deleted'
    | 'gone'
    | 'failed'
    | 'skipped'
    | null
  provider_event_error?: string | null

  // Confirmation-email send outcome (added Phase 0 Task 4).
  last_email_status?: 'sent' | 'sent-no-attachments' | 'failed' | 'skipped' | null
  last_email_error?: string | null
  last_email_at?: string | null
}

export type BookingsStatus = 'idle' | 'loading' | 'success' | 'error'

export interface UseBookingsResult {
  rows: BookingRow[]
  status: BookingsStatus
  error: string | null
  refetch: () => void
}

/**
 * Fetches the caller's bookings via the `list-bookings` edge function,
 * which validates the user JWT server-side and queries with the service
 * role (bookings has RLS enabled with no policies, so direct table reads
 * from the anon/authenticated context return zero rows). Sorted by
 * datetime_iso DESC at the function.
 */
export function useBookings(userId: string | null | undefined): UseBookingsResult {
  const [rows, setRows] = useState<BookingRow[]>([])
  const [status, setStatus] = useState<BookingsStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)
  const cancelledRef = useRef(false)

  const refetch = useCallback(() => setTick((n) => n + 1), [])

  useEffect(() => {
    if (!userId) {
      setRows([])
      setStatus('idle')
      setError(null)
      return
    }

    cancelledRef.current = false
    setStatus('loading')
    setError(null)

    void (async () => {
      const { data, error: fnError } = await supabase.functions.invoke('list-bookings')
      if (cancelledRef.current) return
      if (fnError) {
        setStatus('error')
        setError(fnError.message)
        setRows([])
        return
      }
      const bookings = (data as { bookings?: BookingRow[] } | null)?.bookings ?? []
      setRows(bookings)
      setStatus('success')
    })()

    return () => {
      cancelledRef.current = true
    }
  }, [userId, tick])

  return { rows, status, error, refetch }
}
